import React from 'react';
import Icon from '../../../components/AppIcon';

const AssessmentPreview = ({ selectedOccupation, selectedSeniority, className = '' }) => {
  const getSeniorityInfo = (seniority) => {
    const seniorityMap = {
      'apprentice': { label: 'Apprentice', duration: '8-10', questions: '6-8' },
      'junior': { label: 'Junior', duration: '10-12', questions: '7-9' },
      'mid': { label: 'Mid-Level', duration: '12-15', questions: '8-10' },
      'senior': { label: 'Senior', duration: '15-18', questions: '9-11' },
      'executive': { label: 'Executive', duration: '18-20', questions: '10-12' }
    };
    return seniorityMap?.[seniority] || { label: 'Unknown', duration: '10-15', questions: '8-10' };
  };

  const getCategoryIcon = (category) => {
    const categoryMap = {
      'trades': 'Wrench',
      'healthcare': 'Heart',
      'corporate': 'Building2',
      'technology': 'Code',
      'creative': 'Palette',
      'freelance': 'Users'
    };
    return categoryMap?.[category] || 'Briefcase';
  };

  if (!selectedOccupation || !selectedSeniority) {
    return (
      <div className={`bg-muted/30 rounded-lg p-8 text-center ${className}`}>
        <Icon name="ClipboardList" size={48} className="text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-medium text-muted-foreground mb-2">Assessment Preview</h3>
        <p className="text-muted-foreground">
          Select your occupation and seniority level to see assessment details
        </p>
      </div>
    );
  }

  const seniorityInfo = getSeniorityInfo(selectedSeniority);
  const categoryIcon = getCategoryIcon(selectedOccupation?.category);

  return (
    <div className={`bg-card border border-border rounded-lg p-6 space-y-6 ${className}`}>
      {/* Header */}
      <div className="text-center space-y-2">
        <div className="flex items-center justify-center space-x-2 mb-3">
          <Icon name="Eye" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-foreground">Assessment Preview</h3>
        </div>
        <p className="text-sm text-muted-foreground">
          Here's what your personalized assessment will include
        </p>
      </div>
      {/* Selected Role Summary */}
      <div className="bg-primary/5 rounded-lg p-4 border border-primary/20">
        <div className="flex items-start space-x-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Icon name={categoryIcon} size={20} className="text-primary" />
          </div>
          <div className="flex-1">
            <h4 className="font-medium text-foreground">{selectedOccupation?.name}</h4>
            <p className="text-sm text-muted-foreground mt-1">{selectedOccupation?.description}</p>
            <div className="flex items-center space-x-4 mt-3">
              <div className="flex items-center space-x-1">
                <Icon name="Award" size={14} className="text-primary" />
                <span className="text-sm font-medium text-primary">{seniorityInfo?.label} Level</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Assessment Details */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-muted/50 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Clock" size={16} className="text-success" />
            <span className="font-medium text-foreground">Duration</span>
          </div>
          <p className="text-2xl font-semibold text-success">{seniorityInfo?.duration} min</p>
          <p className="text-sm text-muted-foreground">Estimated completion time</p>
        </div>

        <div className="bg-muted/50 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="HelpCircle" size={16} className="text-warning" />
            <span className="font-medium text-foreground">Questions</span>
          </div>
          <p className="text-2xl font-semibold text-warning">{seniorityInfo?.questions}</p>
          <p className="text-sm text-muted-foreground">Role-specific questions</p>
        </div>
      </div>
      {/* Assessment Components */}
      <div className="space-y-3">
        <h4 className="font-medium text-foreground">Assessment Components:</h4>
        <div className="space-y-2">
          <div className="flex items-center space-x-3 p-3 bg-muted/30 rounded-lg">
            <Icon name="Target" size={16} className="text-primary" />
            <div className="flex-1">
              <span className="font-medium text-foreground">Role-Specific Questions</span>
              <p className="text-sm text-muted-foreground">
                Tailored questions about your current role, challenges, and goals
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3 p-3 bg-muted/30 rounded-lg">
            <Icon name="BarChart3" size={16} className="text-secondary" />
            <div className="flex-1">
              <span className="font-medium text-foreground">Attitude Assessment</span>
              <p className="text-sm text-muted-foreground">
                Job satisfaction, stress levels, confidence, and career momentum
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3 p-3 bg-muted/30 rounded-lg">
            <Icon name="Brain" size={16} className="text-accent" />
            <div className="flex-1">
              <span className="font-medium text-foreground">AI Analysis</span>
              <p className="text-sm text-muted-foreground">
                Personalized insights and development recommendations
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* What You'll Get */}
      <div className="bg-success/5 rounded-lg p-4 border border-success/20">
        <div className="flex items-center space-x-2 mb-3">
          <Icon name="Gift" size={16} className="text-success" />
          <span className="font-medium text-foreground">What You'll Receive:</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {[
            'Personalized career development plan',
            'Skills gap analysis and recommendations',
            'Weekly action items and exercises',
            'Progress tracking and milestones',
            'Networking and mindset guidance',
            'Industry-specific KPIs and metrics'
          ]?.map((benefit, index) => (
            <div key={index} className="flex items-center space-x-2">
              <Icon name="Check" size={14} className="text-success flex-shrink-0" />
              <span className="text-sm text-muted-foreground">{benefit}</span>
            </div>
          ))}
        </div>
      </div>
      {/* Privacy Note */}
      <div className="text-center">
        <div className="inline-flex items-center space-x-2 text-sm text-muted-foreground bg-muted/30 rounded-full px-4 py-2">
          <Icon name="Shield" size={14} className="text-primary" />
          <span>Your responses are private and secure</span>
        </div>
      </div>
    </div>
  );
};

export default AssessmentPreview;